import React from 'react';
import { Link } from 'react-router-dom';

const disciplines = [
  {
    title: 'AI Engineering',
    description: 'Agent orchestration, retrieval, evals and guardrails. We build the AI layer end to end.',
    tags: ['LangGraph', 'RAG', 'Evals', 'Guardrails'],
  },
  {
    title: 'Platform and MLOps',
    description: 'Infrastructure as code, CI/CD with eval gates, and observability from day one.',
    tags: ['Terraform', 'AWS', 'OpenTelemetry', 'Docker'],
  },
  {
    title: 'Voice and Real-time',
    description: 'Streaming speech, telephony and sub-second latency for voice AI that feels natural.',
    tags: ['Deepgram', 'ElevenLabs', 'Twilio', 'WebRTC'],
  },
  {
    title: 'Product Engineering and Design',
    description: 'Full-stack web and mobile, APIs and backends. We ship the whole product.',
    tags: ['Next.js', 'FastAPI', 'Mobile', 'Figma'],
  },
  {
    title: 'Security and Compliance',
    description: 'Threat modelling, DPIAs, BAA, and technical documentation for EU AI Act, SOC 2 and HIPAA.',
    tags: ['DPIA', 'SOC 2', 'HIPAA', 'EU AI Act'],
  },
  {
    title: 'Growth Engineering',
    description: 'Instrumentation, experiments and funnel analytics so you can see what AI actually moves.',
    tags: ['Analytics', 'A/B testing', 'Attribution'],
  },
];

const principles = [
  {
    title: 'Small and accountable',
    description: 'The same people who run the AI Audit write the code and run the system. No hand-offs, no account managers.',
  },
  {
    title: 'Metric-first',
    description: 'We agree the number we are improving before we write a line of code, then report against it monthly.',
  },
  {
    title: 'Compliance by design',
    description: 'Threat models and data-flow reviews happen before features, not after. Your compliance team can sign it off.',
  },
  {
    title: 'You own everything',
    description: 'Code, cloud, data, models. No lock-in, no licence fees. Stop at any step and keep everything we built.',
  },
];

const timeline = [
  {
    year: '2025',
    text: 'Zwift Labs founded. First client: multi-agent financial guidance system for Gains (agentic architecture, retrieval, guardrails).',
  },
  {
    year: '2026',
    text: 'AwesomeHires launched. AI-powered recruiting product: CV parsing, criteria scoring and recruiter outreach.',
  },
];

const About: React.FC = () => {
  return (
    <>
      <section className="section-padding bg-white">
        <div className="container-max">
          <p className="text-sm text-neutral-500 mb-3">Company</p>
          <h1 className="text-4xl md:text-5xl font-semibold text-black leading-[1.05] tracking-[-0.02em] mb-5 max-w-3xl">
            An engineering team built for regulated AI.
          </h1>
          <p className="text-lg text-neutral-600 leading-relaxed max-w-2xl">
            One accountable team across six disciplines. The same people from AI Audit to build to run.
          </p>
        </div>
      </section>

      <section className="section-padding bg-neutral-50">
        <div className="container-max">
          <h2 className="text-3xl font-semibold text-black tracking-tight mb-10">
            Six disciplines, one team
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {disciplines.map((d) => (
              <div key={d.title} className="bg-white rounded-2xl p-6 border border-neutral-200">
                <h3 className="text-lg font-semibold text-black mb-2">{d.title}</h3>
                <p className="text-neutral-600 leading-relaxed mb-4">{d.description}</p>
                <div className="flex flex-wrap gap-2">
                  {d.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center text-xs font-medium text-neutral-500 bg-neutral-100 px-2.5 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-max">
          <h2 className="text-4xl md:text-5xl font-semibold text-black leading-[1.05] tracking-[-0.02em] mb-10">
            How we operate
          </h2>
          <div className="grid md:grid-cols-2 gap-10">
            {principles.map((p) => (
              <div key={p.title}>
                <h3 className="text-xl font-semibold text-black mb-3 tracking-tight">{p.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-neutral-50">
        <div className="container-max">
          <h2 className="text-4xl md:text-5xl font-semibold text-black leading-[1.05] tracking-[-0.02em] mb-10">
            Track record
          </h2>
          <div className="space-y-8 max-w-2xl">
            {timeline.map((item) => (
              <div key={item.year} className="flex gap-8">
                <span className="text-2xl font-semibold text-neutral-300 shrink-0 w-16">{item.year}</span>
                <p className="text-neutral-600 leading-relaxed pt-1">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-max">
          <p className="text-lg text-neutral-600 mb-6">Ready to work with us?</p>
          <a
            href="https://calendar.app.google/cnN9KoLp6WZYMX9G7"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-black hover:bg-neutral-800 text-white font-medium px-7 py-3.5 rounded-full transition-colors"
          >
            Book a fit call
          </a>
        </div>
      </section>
    </>
  );
};

export default About;
